import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  AlertTriangle,
  Plus,
  Trash2,
  Target,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Save,
  RotateCcw,
} from "lucide-react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { API } from "../utils/api";

const formatCurrency = (value, currency = "EUR") => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

export default function OnTrack({ user }) {
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [targetYear, setTargetYear] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable overrides (kept separate from persisted adjustments until "Save")
  const [monthlyOverride, setMonthlyOverride] = useState("");
  const [annualOverride, setAnnualOverride] = useState("");
  const [returnOverride, setReturnOverride] = useState("");
  const [remainingMonthsOverride, setRemainingMonthsOverride] = useState("");
  const [extraExpenses, setExtraExpenses] = useState([]);
  const [extraSavings, setExtraSavings] = useState([]);
  const [newExpense, setNewExpense] = useState({ description: "", amount: "" });
  const [newSaving, setNewSaving] = useState({ description: "", amount: "" });

  const currency = user?.default_currency || "EUR";

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    if (selectedPlanId) fetchOnTrack(selectedPlanId, targetYear);
  }, [selectedPlanId, targetYear]);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/future-plans`);
      setPlans(res.data || []);
      if (res.data && res.data.length > 0) {
        setSelectedPlanId(res.data[0].plan_id);
      }
    } catch (err) {
      console.error("Failed to load plans:", err);
      toast.error("Failed to load future plans");
    } finally {
      setLoading(false);
    }
  };

  const fetchOnTrack = async (planId, year) => {
    try {
      setLoading(true);
      const url = year
        ? `${API}/future-plans/${planId}/on-track?target_year=${year}`
        : `${API}/future-plans/${planId}/on-track`;
      const res = await axios.get(url);
      setStatus(res.data);
      // Hydrate editable fields from persisted adjustments
      const adj = res.data.adjustments || {};
      setMonthlyOverride(
        adj.monthly_savings_override !== null && adj.monthly_savings_override !== undefined
          ? String(adj.monthly_savings_override)
          : ""
      );
      setAnnualOverride(
        adj.annual_savings_override !== null && adj.annual_savings_override !== undefined
          ? String(adj.annual_savings_override)
          : ""
      );
      setReturnOverride(
        adj.weighted_return_override !== null && adj.weighted_return_override !== undefined
          ? String(adj.weighted_return_override)
          : ""
      );
      setRemainingMonthsOverride(
        adj.remaining_months_override !== null && adj.remaining_months_override !== undefined
          ? String(adj.remaining_months_override)
          : ""
      );
      setExtraExpenses(adj.extra_expenses || []);
      setExtraSavings(adj.extra_savings || []);
    } catch (err) {
      console.error("Failed to compute on-track:", err);
      toast.error("Failed to compute status");
    } finally {
      setLoading(false);
    }
  };

  const addExtraItem = (kind) => {
    const src = kind === "expense" ? newExpense : newSaving;
    if (!src.description || !src.amount) {
      toast.error(`Enter description and amount for ${kind}`);
      return;
    }
    const item = {
      id: `tmp_${Date.now()}`,
      description: src.description,
      amount: parseFloat(src.amount) || 0,
    };
    if (kind === "expense") {
      setExtraExpenses((prev) => [...prev, item]);
      setNewExpense({ description: "", amount: "" });
    } else {
      setExtraSavings((prev) => [...prev, item]);
      setNewSaving({ description: "", amount: "" });
    }
  };

  const removeExtraItem = (kind, id) => {
    if (kind === "expense") {
      setExtraExpenses((prev) => prev.filter((i) => i.id !== id));
    } else {
      setExtraSavings((prev) => prev.filter((i) => i.id !== id));
    }
  };

  const saveAdjustments = async () => {
    if (!selectedPlanId) return;
    try {
      setSaving(true);
      const payload = {
        monthly_savings_override:
          monthlyOverride === "" ? null : parseFloat(monthlyOverride),
        annual_savings_override:
          annualOverride === "" ? null : parseFloat(annualOverride),
        weighted_return_override:
          returnOverride === "" ? null : parseFloat(returnOverride),
        remaining_months_override:
          remainingMonthsOverride === "" ? null : parseInt(remainingMonthsOverride, 10),
        extra_expenses: extraExpenses.map((i) => ({
          id: i.id && !i.id.startsWith("tmp_") ? i.id : null,
          description: i.description,
          amount: Number(i.amount) || 0,
        })),
        extra_savings: extraSavings.map((i) => ({
          id: i.id && !i.id.startsWith("tmp_") ? i.id : null,
          description: i.description,
          amount: Number(i.amount) || 0,
        })),
      };
      const res = await axios.put(
        `${API}/future-plans/${selectedPlanId}/on-track/adjustments${targetYear ? `?target_year=${targetYear}` : ""}`,
        payload
      );
      setStatus(res.data);
      const adj = res.data.adjustments || {};
      setExtraExpenses(adj.extra_expenses || []);
      setExtraSavings(adj.extra_savings || []);
      toast.success("Check updated & saved");
    } catch (err) {
      console.error("Failed to save adjustments:", err);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const resetAdjustments = async () => {
    if (!selectedPlanId) return;
    try {
      setSaving(true);
      const payload = {
        monthly_savings_override: null,
        annual_savings_override: null,
        weighted_return_override: null,
        remaining_months_override: null,
        extra_expenses: [],
        extra_savings: [],
      };
      const res = await axios.put(
        `${API}/future-plans/${selectedPlanId}/on-track/adjustments${targetYear ? `?target_year=${targetYear}` : ""}`,
        payload
      );
      setStatus(res.data);
      setMonthlyOverride("");
      setAnnualOverride("");
      setReturnOverride("");
      setRemainingMonthsOverride("");
      setExtraExpenses([]);
      setExtraSavings([]);
      toast.success("Adjustments cleared");
    } catch (err) {
      console.error("Failed to reset:", err);
      toast.error("Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  const progressPct = useMemo(() => {
    if (!status || !status.target_balance) return 0;
    return Math.max(0, Math.min(120, (status.projected_balance / status.target_balance) * 100));
  }, [status]);

  if (loading && !status) {
    return (
      <Layout user={user} pageTitle="Am I On Track?">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (plans.length === 0) {
    return (
      <Layout user={user} pageTitle="Am I On Track?">
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-6">
              <Target className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">No Plans Yet</h3>
            <p className="text-gray-400 mb-6">
              Create a Future Plan first, then come back here to check your progress.
            </p>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  const onTrack = status?.on_track;

  return (
    <Layout user={user} pageTitle="Am I On Track?">
      <div data-testid="on-track-page" className="space-y-6">
        {/* Header / Plan selector */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className="text-gray-400 max-w-xl">
            A live check of your year-end goal. We use your current account balances, the remaining
            months of contributions, expected returns, and any extra items you add below.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="w-full sm:w-60">
              <Label className="text-gray-300 text-xs uppercase tracking-wider">Plan</Label>
              <Select value={selectedPlanId} onValueChange={(v) => { setSelectedPlanId(v); setTargetYear(""); }}>
                <SelectTrigger
                  className="mt-1 bg-secondary border-border text-white"
                  data-testid="on-track-plan-select"
                >
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {plans.map((p) => (
                    <SelectItem
                      key={p.plan_id}
                      value={p.plan_id}
                      className="text-gray-300 focus:text-white focus:bg-secondary"
                    >
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {status && (status.available_target_years || []).length > 0 && (
              <div className="w-full sm:w-44">
                <Label className="text-gray-300 text-xs uppercase tracking-wider">Target year</Label>
                <Select
                  value={String(targetYear || status.target_year)}
                  onValueChange={(v) => setTargetYear(v)}
                >
                  <SelectTrigger
                    className="mt-1 bg-secondary border-border text-white"
                    data-testid="on-track-year-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-72">
                    {(status.available_target_years || []).map((y) => (
                      <SelectItem
                        key={y}
                        value={String(y)}
                        className="text-gray-300 focus:text-white focus:bg-secondary"
                      >
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {status && (
          <>
            {/* Verdict banner */}
            <motion.div
              key={`${status.plan_id}-${onTrack}`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card
                className={`border-2 ${
                  onTrack
                    ? "bg-green-500/10 border-green-500/40"
                    : "bg-red-500/10 border-red-500/40"
                }`}
                data-testid="on-track-verdict-card"
              >
                <CardContent className="p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                      onTrack ? "bg-green-500/20" : "bg-red-500/20"
                    }`}
                  >
                    {onTrack ? (
                      <CheckCircle2 className="w-8 h-8 text-green-400" />
                    ) : (
                      <AlertTriangle className="w-8 h-8 text-red-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h2
                      className={`text-2xl font-bold ${onTrack ? "text-green-300" : "text-red-300"}`}
                      data-testid="on-track-verdict-text"
                    >
                      {onTrack ? "On track!" : "Off track"}
                    </h2>
                    <p className="text-gray-300 mt-1">
                      Projected to reach{" "}
                      <span className="font-semibold text-white number-display">
                        {formatCurrency(status.projected_balance, currency)}
                      </span>{" "}
                      by end of {status.target_year} vs target{" "}
                      <span className="font-semibold text-white number-display">
                        {formatCurrency(status.target_balance, currency)}
                      </span>
                    </p>
                    <p
                      className={`text-sm mt-1 number-display ${
                        onTrack ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {onTrack ? "Ahead by " : "Short by "}
                      {formatCurrency(Math.abs(status.delta), currency)} ({status.pct_of_target}% of
                      target)
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Progress bar */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Progress toward target</span>
                  <span className="text-white font-semibold">{status.pct_of_target}%</span>
                </div>
                <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(progressPct, 100)}%` }}
                    transition={{ duration: 0.6 }}
                    className={`h-full ${onTrack ? "bg-green-500" : "bg-amber-500"}`}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-2 number-display">
                  <span>{formatCurrency(0, currency)}</span>
                  <span>Target: {formatCurrency(status.target_balance, currency)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Key numbers grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-card border-border" data-testid="on-track-current-card">
                <CardContent className="p-5">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Current balance</p>
                  <p className="text-2xl font-bold text-amber-400 number-display mt-1">
                    {formatCurrency(status.current_balance, currency)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Live sum of plan's selected accounts</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border" data-testid="on-track-projected-card">
                <CardContent className="p-5">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">
                    Projected end of {status.target_year}
                  </p>
                  <p className="text-2xl font-bold text-green-400 number-display mt-1">
                    {formatCurrency(status.projected_balance, currency)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {status.target_year === status.current_year
                      ? `${status.remaining_months} month${status.remaining_months === 1 ? "" : "s"} remaining`
                      : `${status.target_year - status.current_year + 1} year projection`}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border" data-testid="on-track-target-card">
                <CardContent className="p-5">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">
                    Target ({status.target_year})
                  </p>
                  <p className="text-2xl font-bold text-blue-400 number-display mt-1">
                    {formatCurrency(status.target_balance, currency)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">From your plan's forecast</p>
                </CardContent>
              </Card>
            </div>

            {/* Breakdown */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold text-white">
                  {status.target_year === status.current_year
                    ? "What's still coming between now and year-end"
                    : `What's coming between now and end of ${status.target_year} (current year only)`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <BreakdownRow
                    label="Monthly savings × months left"
                    value={`+${formatCurrency(
                      status.breakdown.monthly_contributions_remaining,
                      currency
                    )}`}
                    hint={`${formatCurrency(
                      status.breakdown.monthly_savings_per_month,
                      currency
                    )} × ${status.remaining_months}`}
                    positive
                  />
                  <BreakdownRow
                    label="Annual savings still due"
                    value={`+${formatCurrency(status.breakdown.annual_savings_remaining, currency)}`}
                    positive
                  />
                  <BreakdownRow
                    label={`Returns (~${status.breakdown.weighted_return_pct}%)`}
                    value={`+${formatCurrency(status.breakdown.total_interest, currency)}`}
                    positive
                  />
                  <BreakdownRow
                    label="Planned one-time income"
                    value={`+${formatCurrency(
                      status.breakdown.one_time_income_remaining,
                      currency
                    )}`}
                    positive
                  />
                  <BreakdownRow
                    label="Planned one-time expenses"
                    value={`-${formatCurrency(
                      status.breakdown.one_time_expenses_remaining,
                      currency
                    )}`}
                  />
                  <BreakdownRow
                    label="Your extra savings"
                    value={`+${formatCurrency(status.breakdown.extra_savings_total, currency)}`}
                    positive
                  />
                  <BreakdownRow
                    label="Your extra expenses"
                    value={`-${formatCurrency(status.breakdown.extra_expenses_total, currency)}`}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Year-by-year projection (only visible when target is beyond current year) */}
            {status.yearly_projection && status.yearly_projection.length > 1 && (
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-bold text-white">
                    Year-by-year projection to {status.target_year}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="yearly-projection-table">
                      <thead>
                        <tr className="border-b border-border text-gray-400">
                          <th className="py-2 text-left font-medium">Year</th>
                          <th className="py-2 text-right font-medium">Starting</th>
                          <th className="py-2 text-right font-medium">Contributions</th>
                          <th className="py-2 text-right font-medium">Returns</th>
                          <th className="py-2 text-right font-medium">Events</th>
                          <th className="py-2 text-right font-medium">Ending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {status.yearly_projection.map((row) => {
                          const events =
                            (row.one_time_income || 0) - (row.one_time_expenses || 0) +
                            (row.extra_savings || 0) - (row.extra_expenses || 0);
                          return (
                            <tr
                              key={row.year}
                              className={`border-b border-border/50 ${
                                row.is_current_year ? "bg-green-500/5" : ""
                              }`}
                            >
                              <td className="py-2 text-white font-medium">
                                {row.year}
                                {row.is_current_year && (
                                  <span className="ml-2 text-xs text-green-400">(now)</span>
                                )}
                              </td>
                              <td className="py-2 text-right text-gray-300 number-display">
                                {formatCurrency(row.starting_balance, currency)}
                              </td>
                              <td className="py-2 text-right text-green-400 number-display">
                                +{formatCurrency(row.contributions, currency)}
                              </td>
                              <td className="py-2 text-right text-blue-400 number-display">
                                +{formatCurrency(row.interest, currency)}
                              </td>
                              <td
                                className={`py-2 text-right number-display ${
                                  events >= 0 ? "text-green-400" : "text-red-400"
                                }`}
                              >
                                {events !== 0
                                  ? `${events > 0 ? "+" : ""}${formatCurrency(events, currency)}`
                                  : "-"}
                              </td>
                              <td className="py-2 text-right font-bold text-white number-display">
                                {formatCurrency(row.ending_balance, currency)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Editable overrides */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-green-400" />
                  Tweak the inputs
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-gray-300 text-xs">
                      Monthly savings ({currency})
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={monthlyOverride}
                      onChange={(e) => setMonthlyOverride(e.target.value)}
                      placeholder={`Plan: ${status.breakdown.monthly_savings_per_month}`}
                      className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                      data-testid="override-monthly-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 text-xs">
                      Annual savings ({currency})
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={annualOverride}
                      onChange={(e) => setAnnualOverride(e.target.value)}
                      placeholder="Leave blank to use plan"
                      className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                      data-testid="override-annual-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 text-xs">Annual return (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={returnOverride}
                      onChange={(e) => setReturnOverride(e.target.value)}
                      placeholder={`Plan: ${status.breakdown.weighted_return_pct}`}
                      className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                      data-testid="override-return-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 text-xs">Remaining months</Label>
                    <Select
                      value={remainingMonthsOverride === "" ? "auto" : remainingMonthsOverride}
                      onValueChange={(v) =>
                        setRemainingMonthsOverride(v === "auto" ? "" : v)
                      }
                    >
                      <SelectTrigger
                        className="mt-1 bg-secondary border-border text-white"
                        data-testid="override-remaining-months-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border max-h-72">
                        <SelectItem
                          value="auto"
                          className="text-gray-300 focus:text-white focus:bg-secondary"
                        >
                          Auto ({status.auto_remaining_months ?? status.remaining_months} left)
                        </SelectItem>
                        {Array.from({ length: 13 }, (_, i) => i).map((n) => (
                          <SelectItem
                            key={n}
                            value={String(n)}
                            className="text-gray-300 focus:text-white focus:bg-secondary"
                          >
                            {n} {n === 1 ? "month" : "months"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Used for current-year projection only.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Extra Expenses */}
                  <div className="rounded-xl border border-border p-4 bg-secondary/30">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingDown className="w-4 h-4 text-red-400" />
                      <h3 className="text-sm font-semibold text-white">Extra expenses coming up</h3>
                    </div>
                    <div className="space-y-2 mb-3">
                      {extraExpenses.length === 0 ? (
                        <p className="text-xs text-gray-500">None added yet.</p>
                      ) : (
                        extraExpenses.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-2 bg-secondary rounded-lg text-sm"
                            data-testid={`extra-expense-row-${item.id}`}
                          >
                            <span className="text-gray-300 truncate">{item.description}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-red-400 number-display">
                                -{formatCurrency(item.amount, currency)}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-gray-500 hover:text-red-400"
                                onClick={() => removeExtraItem("expense", item.id)}
                                data-testid={`remove-expense-${item.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Description"
                        value={newExpense.description}
                        onChange={(e) =>
                          setNewExpense({ ...newExpense, description: e.target.value })
                        }
                        className="bg-secondary border-border text-white placeholder:text-gray-500"
                        data-testid="new-expense-desc"
                      />
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={newExpense.amount}
                        onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                        className="w-28 bg-secondary border-border text-white placeholder:text-gray-500"
                        data-testid="new-expense-amount"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
                        onClick={() => addExtraItem("expense")}
                        data-testid="add-expense-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Extra Savings */}
                  <div className="rounded-xl border border-border p-4 bg-secondary/30">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <h3 className="text-sm font-semibold text-white">Extra savings expected</h3>
                    </div>
                    <div className="space-y-2 mb-3">
                      {extraSavings.length === 0 ? (
                        <p className="text-xs text-gray-500">None added yet.</p>
                      ) : (
                        extraSavings.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-2 bg-secondary rounded-lg text-sm"
                            data-testid={`extra-saving-row-${item.id}`}
                          >
                            <span className="text-gray-300 truncate">{item.description}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-green-400 number-display">
                                +{formatCurrency(item.amount, currency)}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-gray-500 hover:text-red-400"
                                onClick={() => removeExtraItem("saving", item.id)}
                                data-testid={`remove-saving-${item.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Description"
                        value={newSaving.description}
                        onChange={(e) =>
                          setNewSaving({ ...newSaving, description: e.target.value })
                        }
                        className="bg-secondary border-border text-white placeholder:text-gray-500"
                        data-testid="new-saving-desc"
                      />
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={newSaving.amount}
                        onChange={(e) => setNewSaving({ ...newSaving, amount: e.target.value })}
                        className="w-28 bg-secondary border-border text-white placeholder:text-gray-500"
                        data-testid="new-saving-amount"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
                        onClick={() => addExtraItem("saving")}
                        data-testid="add-saving-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={resetAdjustments}
                    disabled={saving}
                    className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
                    data-testid="reset-adjustments-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Clear adjustments
                  </Button>
                  <Button
                    onClick={saveAdjustments}
                    disabled={saving}
                    className="bg-green-500 hover:bg-green-400 text-black rounded-full font-semibold"
                    data-testid="save-adjustments-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Saving..." : "Save & recalculate"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}

function BreakdownRow({ label, value, hint, positive }) {
  return (
    <div className="p-3 bg-secondary rounded-xl">
      <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
      <p
        className={`text-base font-bold number-display mt-1 ${
          positive ? "text-green-400" : "text-red-400"
        }`}
      >
        {value}
      </p>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}
